/**
 * EnhancedDashboardWidgets — Widgets opérationnels du Dashboard.
 *
 * UX optimisée : tuiles compactes avec scroll interne, grid responsive
 * (1 col mobile, 2 cols tablette, 4 cols desktop), hauteurs fixes pour
 * tenir dans un viewport sans scroll global.
 *
 * Pilote par un seul endpoint /api/dashboard/overview pour minimiser
 * les round-trips.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Calendar, FileText, ClipboardCheck, Mail, Plus,
  TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Award,
  Briefcase, Users, Sparkles, Eye, EyeOff, Settings2,
  ShieldCheck, Clock, ArrowRight, Euro, UserCog, X,
  BookmarkPlus, Trash2,
} from 'lucide-react';
import api from '../api/client';

// ---- Helpers --------------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return '—'; }
};
const fmtMoney = (n) => (typeof n === 'number' ? n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—');

// ---- Tile wrapper (uniforme, hauteur fixe + scroll interne) ---------------
function Tile({ title, icon: Icon, iconColor = 'indigo', children, action, testId, scroll = true, center = false }) {
  const colors = {
    indigo: 'text-indigo-500',
    amber: 'text-amber-500',
    emerald: 'text-emerald-500',
    violet: 'text-violet-500',
    rose: 'text-rose-500',
    sky: 'text-sky-500',
  };
  return (
    <div data-testid={testId} className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-2 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${colors[iconColor]}`} />}
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">{title}</h3>
        </div>
        {action}
      </div>
      <div className={`flex-1 min-h-0 flex flex-col ${center ? 'items-center justify-center text-center' : ''} ${scroll ? 'overflow-y-auto -mr-1 pr-1' : 'overflow-hidden'}`}>
        {children}
      </div>
    </div>
  );
}

// ---- Empty state centré verticalement (réutilisable) ----------------------
function Empty({ text }) {
  return (
    <div className="flex-1 w-full flex items-center justify-center text-[11px] text-slate-400 italic">
      {text}
    </div>
  );
}

// ---- Bandeau "À faire" (full-width, en tête) ------------------------------
export function TodosBanner({ todos }) {
  if (!todos || todos.total === 0) {
    return (
      <div data-testid="dashboard-todos-empty" className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 flex items-center gap-2 text-emerald-900 flex-shrink-0">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
        <span className="text-[11.5px] font-semibold">Tout est à jour ! 🎉</span>
      </div>
    );
  }
  const items = [
    { count: todos.unsigned_conventions, label: 'conventions', to: '/app/sessions', icon: FileText, color: 'amber' },
    { count: todos.overdue_invoices, label: 'factures en retard', to: '/app/factures', icon: Euro, color: 'red' },
    { count: todos.missing_attendance, label: 'émargements', to: '/app/emargements', icon: ClipboardCheck, color: 'orange' },
    { count: todos.pending_quotes, label: 'devis en attente', to: '/app/devis', icon: Mail, color: 'blue' },
  ].filter((x) => x.count > 0);
  const colors = {
    amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-900',
    red: 'bg-red-50 border-red-200 hover:bg-red-100 text-red-900',
    orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100 text-orange-900',
    blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-900',
  };
  return (
    <div data-testid="dashboard-todos" className="flex items-center gap-2.5 flex-wrap flex-shrink-0">
      <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-md">
        <AlertTriangle className="w-3.5 h-3.5" /> Alertes ({todos.total})
      </div>
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <Link
            key={i}
            to={it.to}
            data-testid={`dashboard-todo-${it.label.replace(/ /g, '-')}`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 transition-colors ${colors[it.color]}`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[12px] font-bold">{it.count}</span>
            <span className="text-[11.5px]">{it.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

// ---- Quick Actions (barre compacte) ---------------------------------------
export function QuickActions() {
  const actions = [
    { label: 'Session', to: '/app/sessions?new=1', icon: Calendar, color: 'indigo' },
    { label: 'Devis', to: '/app/devis?new=1', icon: FileText, color: 'violet' },
    { label: 'Opportunité', to: '/app/crm?new=deal', icon: Briefcase, color: 'rose' },
    { label: 'Apprenant', to: '/app/stagiaires?new=1', icon: Users, color: 'emerald' },
    { label: 'Entreprise', to: '/app/entreprises?new=1', icon: Briefcase, color: 'sky' },
    { label: 'Email', to: '/app/email?compose=1', icon: Mail, color: 'amber' },
  ];
  const colors = {
    indigo: 'border-indigo-200 hover:bg-indigo-50 text-[#4338CA]',
    violet: 'border-violet-200 hover:bg-violet-50 text-violet-700',
    rose: 'border-rose-200 hover:bg-rose-50 text-rose-700',
    emerald: 'border-emerald-200 hover:bg-emerald-50 text-emerald-700',
    sky: 'border-sky-200 hover:bg-sky-50 text-sky-700',
    amber: 'border-amber-200 hover:bg-amber-50 text-amber-700',
  };
  return (
    <div data-testid="dashboard-quick-actions" className="flex items-center gap-2 flex-wrap flex-shrink-0">
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mr-1">+ Nouveau :</span>
      {actions.map((a) => {
        const Icon = a.icon;
        return (
          <Link
            key={a.label}
            to={a.to}
            data-testid={`quick-action-${a.label.toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 rounded-md border bg-white px-2.5 py-1.5 text-[12px] font-semibold transition-all ${colors[a.color]}`}
          >
            <Icon className="w-3.5 h-3.5" /> {a.label}
          </Link>
        );
      })}
    </div>
  );
}

// ---- Pipeline (mini-grille 4 buckets) -------------------------------------
export function PipelineWidget({ pipeline }) {
  if (!pipeline) return null;
  return (
    <Tile testId="dashboard-pipeline" title="Pipeline" icon={TrendingUp} iconColor="indigo"
          action={<Link to="/app/devis" className="text-[10px] text-[#4F46E5] hover:underline font-semibold">Voir →</Link>}
          scroll={false}>
      <div className="grid grid-cols-2 gap-1 flex-1 min-h-0">
        <Link to="/app/devis?status=draft" className="flex flex-col items-center justify-center rounded-md bg-slate-50 hover:bg-slate-100 min-h-0">
          <div className="text-[13px] font-bold text-slate-700 leading-none">{pipeline.draft}</div>
          <div className="text-[9px] text-slate-500 mt-0.5">Brouillons</div>
        </Link>
        <Link to="/app/devis?status=sent" className="flex flex-col items-center justify-center rounded-md bg-blue-50 hover:bg-blue-100 min-h-0">
          <div className="text-[13px] font-bold text-blue-700 leading-none">{pipeline.sent}</div>
          <div className="text-[9px] text-blue-600 mt-0.5">Envoyés</div>
        </Link>
        <Link to="/app/devis?status=negotiating" className="flex flex-col items-center justify-center rounded-md bg-amber-50 hover:bg-amber-100 min-h-0">
          <div className="text-[13px] font-bold text-amber-700 leading-none">{pipeline.negotiating}</div>
          <div className="text-[9px] text-amber-600 mt-0.5">Négo</div>
        </Link>
        <Link to="/app/devis?status=signed" className="flex flex-col items-center justify-center rounded-md bg-emerald-50 hover:bg-emerald-100 min-h-0">
          <div className="text-[13px] font-bold text-emerald-700 leading-none">{pipeline.signed_month}</div>
          <div className="text-[9px] text-emerald-600 mt-0.5">Signés/mois</div>
        </Link>
      </div>
      <div className="rounded-md bg-gradient-to-r from-indigo-50 to-violet-50 px-2 py-1 flex items-center justify-between mt-1 flex-shrink-0">
        <div className="text-[9px] text-[#4338CA] font-semibold">Potentiel</div>
        <div className="text-[11px] font-bold text-indigo-900">{fmtMoney(pipeline.potential_ht)}</div>
      </div>
    </Tile>
  );
}

// ---- Week Agenda (liste scrollable) ---------------------------------------
export function WeekAgendaWidget({ agenda }) {
  return (
    <Tile testId="dashboard-week-agenda" title="7 prochains jours" icon={Calendar} iconColor="indigo">
      {(!agenda || agenda.length === 0) ? (
        <Empty text="Aucun événement." />
      ) : (
        <ul className="space-y-1 flex-1 flex flex-col justify-center">
          {agenda.map((ev, i) => {
            const isAmber = ev.color === 'amber';
            return (
              <li key={i}>
                <Link
                  to={ev.link}
                  className={`flex items-center gap-2 rounded-md border ${isAmber ? 'border-amber-200 bg-amber-50' : 'border-indigo-100 bg-indigo-50/50'} px-2 py-1.5 hover:shadow-sm transition-all`}
                >
                  <Clock className={`w-3 h-3 flex-shrink-0 ${isAmber ? 'text-amber-600' : 'text-[#4F46E5]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] font-semibold text-slate-900 truncate">{ev.title}</div>
                    <div className="text-[10px] text-slate-500">{fmtDate(ev.datetime)}</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Tile>
  );
}

// ---- Qualiopi (gauge compact) ---------------------------------------------
export function QualiopiWidget({ qualiopi }) {
  const total = qualiopi?.total || 0;
  const complete = total - (qualiopi?.incomplete || 0);
  const pct = total ? Math.round((complete / total) * 100) : 0;
  return (
    <Tile testId="dashboard-qualiopi" title="Qualiopi" icon={ShieldCheck} iconColor="emerald"
          action={<Link to="/app/qualiopi" className="text-[10.5px] text-[#4F46E5] hover:underline font-semibold">Voir →</Link>}
          scroll={false}>
      {total === 0 ? (
        <div className="text-[11px] text-slate-400 py-3 text-center">Pas encore configuré.</div>
      ) : (
        <>
          <div className="text-center my-1">
            <div className="text-[26px] font-bold text-emerald-600 leading-none">{pct}%</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{complete} / {total} complets</div>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-1.5">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {qualiopi.incomplete > 0 ? (
            <Link to="/app/qualiopi" className="block text-center rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900 text-[10.5px] font-semibold py-1">
              {qualiopi.incomplete} à compléter
            </Link>
          ) : (
            <div className="text-center text-[10.5px] font-semibold text-emerald-900">✨ Conformité parfaite</div>
          )}
        </>
      )}
    </Tile>
  );
}

// ---- Top Clients + Churn --------------------------------------------------
export function TopClientsWidget({ topClients, churnRisk }) {
  return (
    <Tile testId="dashboard-top-clients" title="Top clients" icon={Award} iconColor="amber">
      {(!topClients || topClients.length === 0) ? (
        <Empty text="Pas encore de facture payée." />
      ) : (
        <>
          <ul className="space-y-0.5 flex-1">
            {topClients.map((c, i) => (
              <li key={c.id}>
                <Link to="/app/entreprises" className="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-slate-50">
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${i === 0 ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                  <span className="text-[11.5px] font-semibold text-slate-900 truncate flex-1">{c.name}</span>
                  <span className="text-[11px] font-bold text-emerald-700 flex-shrink-0">{fmtMoney(c.total_ht)}</span>
                </Link>
              </li>
            ))}
          </ul>
          {churnRisk && churnRisk.length > 0 && (
            <div className="border-t border-slate-100 pt-1.5 mt-auto flex-shrink-0">
              <div className="text-[10px] font-bold text-red-600 mb-0.5">⚠ Inactifs ({churnRisk.length})</div>
              <ul className="space-y-0">
                {churnRisk.slice(0, 2).map((c) => (
                  <li key={c.id} className="text-[10.5px] text-slate-600 truncate">• {c.name}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Tile>
  );
}

// ---- Team workload --------------------------------------------------------
export function TeamWidget({ team }) {
  return (
    <Tile testId="dashboard-team" title="Formateurs · 7j" icon={UserCog} iconColor="violet" center>
      {(!team || team.length === 0) ? (
        <Empty text="Aucune session planifiée." />
      ) : (
        <ul className="space-y-1 flex-1 flex flex-col justify-center w-full">
          {team.map((t) => (
            <li key={t.id}>
              <Link to="/app/formateurs" className="flex items-center justify-center gap-2 rounded px-1 py-1 hover:bg-slate-50">
                <span className="text-[11.5px] font-semibold text-slate-900 truncate">{t.name}</span>
                <span className="text-[10.5px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {t.sessions_this_week}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  );
}

// ---- Streak ---------------------------------------------------------------
export function StreakWidget({ streak }) {
  if (!streak) return null;
  const up = streak.delta_pct == null ? null : streak.delta_pct >= 0;
  return (
    <Tile testId="dashboard-streak" title="Performance hebdo" icon={Sparkles} iconColor="indigo" scroll={false} center>
      <div className="text-[28px] font-bold text-indigo-900 leading-none">{streak.actions_this_week}</div>
      <div className="text-[10.5px] text-[#4338CA] mt-1">action{streak.actions_this_week > 1 ? 's' : ''}</div>
      {up !== null && (
        <div className={`mt-2 inline-flex items-center gap-0.5 text-[10.5px] font-semibold ${up ? 'text-emerald-700' : 'text-red-700'}`}>
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {up ? '+' : ''}{streak.delta_pct}% vs S-1
        </div>
      )}
    </Tile>
  );
}

// ---- Preset picker (vues métier prédéfinies) ------------------------------
const PRESET_ICONS = {
  commercial: '💼',
  qualiopi: '✅',
  finance: '💶',
  trainer: '🎓',
};

export function PresetPicker({ onApply, currentLayoutSnapshot }) {
  const [presets, setPresets] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('⭐');
  const [savingView, setSavingView] = useState(false);

  const refreshAll = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get('/dashboard/layout/presets').then((r) => r.data?.presets || []),
        api.get('/dashboard/custom-views').then((r) => r.data?.views || []),
      ]);
      setPresets(p);
      setCustomViews(c);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (open) refreshAll();
  }, [open]);

  const applyPresetId = async (pid) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/dashboard/layout/apply-preset/${pid}`);
      onApply?.(data);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const applyCustomView = async (vid) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/dashboard/custom-views/${vid}/apply`);
      onApply?.(data);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentView = async () => {
    if (!newName.trim()) return;
    setSavingView(true);
    try {
      const payload = { name: newName.trim(), emoji: newEmoji || '⭐' };
      // Snapshot fallback in case backend's user_preferences isn't up to date yet
      if (currentLayoutSnapshot) {
        if (currentLayoutSnapshot.layout) payload.layout = currentLayoutSnapshot.layout;
        if (currentLayoutSnapshot.grid) payload.grid = currentLayoutSnapshot.grid;
      }
      await api.post('/dashboard/custom-views', payload);
      setNewName('');
      setNewEmoji('⭐');
      setSaveDialogOpen(false);
      refreshAll();
    } finally {
      setSavingView(false);
    }
  };

  const deleteCustomView = async (vid, name) => {
    if (!window.confirm(`Supprimer la vue « ${name} » ?`)) return;
    try {
      await api.delete(`/dashboard/custom-views/${vid}`);
      refreshAll();
    } catch { /* silent */ }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="dashboard-preset-btn"
        className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-slate-200 bg-white hover:bg-slate-50 px-2 py-1 text-slate-700"
        title="Appliquer une vue prédéfinie ou personnalisée"
      >
        <Sparkles className="w-3 h-3" /> Vues
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div
            data-testid="dashboard-preset-panel"
            className="absolute right-0 top-8 z-30 w-80 rounded-lg border border-slate-200 bg-white shadow-xl py-2 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-3 pb-2 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[12px] font-bold text-slate-900">Vues du dashboard</div>
                <div className="text-[10.5px] text-slate-500">Disposition optimisée par profil</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Save current view button */}
            <div className="px-3 py-2 border-b border-slate-100">
              <button
                onClick={() => setSaveDialogOpen(true)}
                data-testid="dashboard-save-view-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-[#4338CA] text-[11.5px] font-semibold px-3 py-1.5"
              >
                <BookmarkPlus className="w-3.5 h-3.5" /> Enregistrer la vue actuelle
              </button>
            </div>

            {/* My custom views */}
            {customViews.length > 0 && (
              <div className="py-1 border-b border-slate-100">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 py-1">Mes vues</div>
                {customViews.map((v) => (
                  <div
                    key={v.id}
                    data-testid={`dashboard-custom-view-${v.id}`}
                    className="group w-full flex items-start gap-2.5 px-3 py-2 hover:bg-indigo-50 transition-colors text-left"
                  >
                    <span className="text-[18px] leading-none mt-0.5">{v.emoji}</span>
                    <button
                      onClick={() => applyCustomView(v.id)}
                      disabled={loading}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="text-[12.5px] font-semibold text-slate-900 truncate">{v.name}</div>
                      <div className="text-[10px] text-[#4F46E5] font-medium mt-0.5">{v.widgets_count} widgets</div>
                    </button>
                    <button
                      onClick={() => deleteCustomView(v.id, v.name)}
                      title="Supprimer"
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* System presets */}
            <div className="py-1">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 py-1">Vues prédéfinies</div>
              {presets.map((p) => (
                <button
                  key={p.id}
                  disabled={loading}
                  onClick={() => applyPresetId(p.id)}
                  data-testid={`dashboard-preset-${p.id}`}
                  className="w-full flex items-start gap-2.5 px-3 py-2 hover:bg-indigo-50 transition-colors disabled:opacity-50 text-left"
                >
                  <span className="text-[18px] leading-none mt-0.5">{PRESET_ICONS[p.id] || '📊'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900">{p.label}</div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">{p.description}</div>
                    <div className="text-[10px] text-[#4F46E5] font-medium mt-1">{p.widgets_count} widgets</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Save dialog */}
      {saveDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSaveDialogOpen(false)}
          data-testid="save-view-dialog"
        >
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[15px] font-semibold text-slate-900">Enregistrer la vue actuelle</h3>
              <button onClick={() => setSaveDialogOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Nom de la vue</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ex. Vue lundi matin"
                  data-testid="save-view-name"
                  autoFocus
                  className="w-full text-[13px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Emoji</label>
                <div className="flex gap-1.5 flex-wrap">
                  {['⭐', '🚀', '📊', '🎯', '💡', '🔥', '⚡', '✨', '📈', '🏆'].map((e) => (
                    <button
                      key={e}
                      onClick={() => setNewEmoji(e)}
                      className={`text-[18px] w-9 h-9 rounded-md border ${newEmoji === e ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setSaveDialogOpen(false)} className="text-[12.5px] rounded-md px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50">
                Annuler
              </button>
              <button
                onClick={saveCurrentView}
                disabled={savingView || !newName.trim()}
                data-testid="save-view-confirm"
                className="text-[12.5px] rounded-md px-3 py-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold disabled:opacity-50"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Customizer panel -----------------------------------------------------
export function LayoutCustomizer({ layout, onToggle, editMode, onToggleEdit, onReset }) {
  const [open, setOpen] = useState(false);
  const labels = {
    todos: 'Alertes',
    quick_actions: 'Création rapide',
    pipeline: 'Pipeline',
    qualiopi: 'Qualiopi',
    top_clients: 'Top clients',
    team: 'Charge formateurs',
    streak: 'Performance hebdo',
    tasks: 'Mes tâches',
    overdue_invoices: 'Factures en retard',
    clients_followup: 'Clients à relancer',
    ca_evolution: 'Évolution du CA',
    recent_signatures: 'Signatures reçues',
    kpi_ca_signe: 'KPI : CA signé (devis)',
    conversion_rate: 'Taux de conversion',
    fill_rate: 'Taux de remplissage',
    sessions_to_sign: 'Sessions à émarger',
    conventions_to_sign: 'Documents à signer',
    unread_emails: 'Emails non lus',
    today_sessions: 'Sessions du jour',
    new_learners_month: 'Nouveaux apprenants',
    distribution: 'Modes de formation',
    upcoming: 'Prochaines sessions',
    activity: 'Activité récente',
    kpi_ca: 'KPI : Chiffre d\'affaires',
    kpi_sessions: 'KPI : Sessions',
    kpi_apprenants: 'KPI : Apprenants',
    kpi_tresorerie: 'KPI : Trésorerie',
    toolbar: 'Barre d\'actions (Alertes + Créer)',
  };
  return (
    <div className="relative flex items-center gap-1.5">
      {editMode && onReset && (
        <button
          onClick={onReset}
          data-testid="dashboard-reset-layout-btn"
          className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-slate-200 bg-white hover:bg-slate-50 px-2 py-1 text-slate-700"
          title="Réinitialiser la disposition"
        >
          Réinitialiser
        </button>
      )}
      {onToggleEdit && (
        <button
          onClick={onToggleEdit}
          data-testid="dashboard-edit-toggle-btn"
          className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border px-2 py-1 transition-all ${
            editMode
              ? 'bg-[#4F46E5] border-indigo-600 text-white shadow-sm'
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          <Settings2 className="w-3 h-3" /> {editMode ? 'Terminer' : 'Personnaliser'}
        </button>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="dashboard-customize-btn"
        className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-slate-200 bg-white hover:bg-slate-50 px-2 py-1 text-slate-700"
        title="Afficher / masquer les tuiles"
      >
        <Eye className="w-3 h-3" /> Tuiles
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div data-testid="dashboard-customize-panel" className="absolute right-0 top-8 z-30 w-56 rounded-lg border border-slate-200 bg-white shadow-xl py-1">
            <div className="px-3 py-1.5 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between">
              <span>Widgets visibles</span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>
            </div>
            {Object.entries(labels).map(([key, lbl]) => (
              <button
                key={key}
                onClick={() => onToggle(key)}
                data-testid={`dashboard-toggle-${key}`}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[11.5px] hover:bg-slate-50"
              >
                <span className="text-slate-700">{lbl}</span>
                {layout[key] !== false ? <Eye className="w-3 h-3 text-emerald-600" /> : <EyeOff className="w-3 h-3 text-slate-300" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---- MergedToolbar : À faire + + Créer ▾ (compact, 1 ligne) ---------------
export function MergedToolbar({ todos }) {
  const [open, setOpen] = useState(false);
  const actions = [
    { label: 'Session', to: '/app/sessions?new=1', icon: Calendar, color: 'text-[#4338CA]' },
    { label: 'Devis', to: '/app/devis?new=1', icon: FileText, color: 'text-violet-700' },
    { label: 'Opportunité', to: '/app/crm?new=deal', icon: Briefcase, color: 'text-rose-700' },
    { label: 'Apprenant', to: '/app/stagiaires?new=1', icon: Users, color: 'text-emerald-700' },
    { label: 'Entreprise', to: '/app/entreprises?new=1', icon: Briefcase, color: 'text-sky-700' },
    { label: 'Email', to: '/app/email?compose=1', icon: Mail, color: 'text-amber-700' },
  ];
  return (
    <div
      data-testid="dashboard-merged-toolbar"
      className="h-full w-full flex items-center gap-2 flex-wrap rounded-xl bg-white border border-[#EFF0F6] px-3 py-2"
    >
      {/* Drag zone à gauche (visible uniquement en mode édition via CSS ::before) */}
      <div className="drag-handle flex-shrink-0 h-6 w-2" title="Glisser pour déplacer" />
      {/* + Créer ▾ button with dropdown */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setOpen(!open)}
          data-testid="dashboard-create-menu-btn"
          className="inline-flex items-center gap-1 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white px-2.5 py-1.5 text-[12px] font-semibold shadow-sm transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Créer
          <ArrowRight className="w-3 h-3 rotate-90" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div
              data-testid="dashboard-create-menu"
              className="absolute left-0 top-9 z-50 w-44 rounded-lg border border-slate-200 bg-white shadow-xl py-1"
            >
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <Link
                    key={a.label}
                    to={a.to}
                    onClick={() => setOpen(false)}
                    data-testid={`quick-action-${a.label.toLowerCase()}`}
                    className="flex items-center gap-2 px-3 py-1.5 text-[12px] font-semibold hover:bg-slate-50"
                  >
                    <Icon className={`w-3.5 h-3.5 ${a.color}`} /> {a.label}
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
      {/* Separator */}
      <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
      {/* À faire pills inline */}
      {todos && <TodosBanner todos={todos} />}
    </div>
  );
}

// ---- Public hook: charge data + layout ------------------------------------
export function useDashboardData() {
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState({});
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null); // timestamp last successful save
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/overview').then((r) => r.data).catch(() => null),
      api.get('/dashboard/layout').then((r) => r.data || {}).catch(() => ({})),
    ]).then(([overview, l]) => {
      setData(overview);
      setLayout(l.layout || {});
      setGrid(l.grid || null);
      setLoading(false);
    });
  }, []);

  const toggleWidget = async (key) => {
    const next = { ...layout, [key]: layout[key] === false ? true : false };
    setLayout(next);
    try {
      await api.put('/dashboard/layout', { layout: next });
      setSavedAt(Date.now());
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || 'Échec de la sauvegarde');
    }
  };

  const saveGrid = async (nextGrid) => {
    // nextGrid expected: { lg: [...], (md/sm/xs optional) }
    setGrid((prev) => ({ ...(prev || {}), ...nextGrid }));
    try {
      await api.put('/dashboard/layout', { grid: nextGrid });
      setSavedAt(Date.now());
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || 'Échec de la sauvegarde');
    }
  };

  const resetLayout = async () => {
    try {
      const { data: d } = await api.post('/dashboard/layout/reset');
      setLayout(d.layout || {});
      setGrid(d.grid || null);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(err?.message || 'Échec de la réinitialisation');
    }
  };

  const applyPreset = (data) => {
    if (!data) return;
    setLayout(data.layout || {});
    setGrid(data.grid || null);
    setSavedAt(Date.now());
  };

  return { data, layout, grid, loading, toggleWidget, saveGrid, resetLayout, applyPreset, savedAt, saveError };
}

// ---- MAIN (legacy export kept for backwards compat) -----------------------
export default function EnhancedDashboardWidgets() {
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/overview').then((r) => r.data).catch(() => null),
      api.get('/dashboard/layout').then((r) => r.data?.layout || {}).catch(() => ({})),
    ]).then(([overview, l]) => {
      setData(overview);
      setLayout(l);
      setLoading(false);
    });
  }, []);

  const toggleWidget = async (key) => {
    const next = { ...layout, [key]: layout[key] === false ? true : false };
    setLayout(next);
    try { await api.put('/dashboard/layout', { layout: next }); } catch { /* silent */ }
  };

  if (loading || !data) return null;
  const show = (k) => layout[k] !== false;

  // Compter widgets de la grille (pour grid auto)
  const gridWidgets = [
    show('pipeline'), show('week_agenda'), show('qualiopi'),
    show('top_clients'), show('team'), show('streak'),
  ].filter(Boolean).length;

  return (
    <div className="space-y-3 flex-shrink-0">
      {/* Toolbar : todos + quick actions + customizer */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {show('todos') && <TodosBanner todos={data.todos} />}
        </div>
        <LayoutCustomizer layout={layout} onToggle={toggleWidget} />
      </div>

      {show('quick_actions') && <QuickActions />}

      {/* Grille de tuiles compactes — toutes uniformes en 6 colonnes sur grand écran */}
      {gridWidgets > 0 && (
        <div
          className="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 auto-rows-[165px]"
          data-testid="dashboard-widgets-grid"
        >
          {show('pipeline') && <PipelineWidget pipeline={data.pipeline} />}
          {show('week_agenda') && <WeekAgendaWidget agenda={data.week_agenda} />}
          {show('qualiopi') && <QualiopiWidget qualiopi={data.qualiopi} />}
          {show('top_clients') && <TopClientsWidget topClients={data.top_clients} churnRisk={data.churn_risk} />}
          {show('team') && <TeamWidget team={data.team} />}
          {show('streak') && <StreakWidget streak={data.streak} />}
        </div>
      )}
    </div>
  );
}
